<script setup>
import { ref, computed } from 'vue'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Plus } from 'lucide-vue-next'

const props = defineProps({
  modelValue: {
    type: String,
    default: '#3182CE'
  }
})

const emit = defineEmits(['update:modelValue'])

// Default preset colors
const defaultColors = [
  '#E53E3E', '#38B2AC', '#3182CE', '#48BB78', '#ED8936',
  '#9F7AEA', '#ED64A6', '#F6AD55', '#4299E1', '#38A169'
]

// Custom colors management
const loadCustomColors = () => {
  const saved = localStorage.getItem('templateCustomColors')
  return saved ? JSON.parse(saved) : []
}

const saveCustomColors = (colors) => {
  localStorage.setItem('templateCustomColors', JSON.stringify(colors))
}

const customColors = ref(loadCustomColors())
const colorPickerRef = ref(null)

// Selected color
const selectedColor = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// Methods
const selectColor = (color) => {
  selectedColor.value = color
}

const addCustomColor = () => {
  if (customColors.value.length < 10 && colorPickerRef.value) {
    colorPickerRef.value.click()
  }
}

const handleColorPickerChange = (e) => {
  const newColor = e.target.value
  if (!customColors.value.includes(newColor) && !defaultColors.includes(newColor)) {
    customColors.value.push(newColor)
    saveCustomColors(customColors.value)
  }
  selectedColor.value = newColor
}

const removeCustomColor = (index) => {
  customColors.value.splice(index, 1)
  saveCustomColors(customColors.value)
}
</script>

<template>
  <Popover>
    <PopoverTrigger as-child>
      <button
        type="button"
        :style="`background-color: ${selectedColor}`"
        class="w-8 h-8 rounded-full border-2 border-gray-800 transition-all hover:scale-105 cursor-pointer"
        :title="selectedColor"
      />
    </PopoverTrigger>
    <PopoverContent class="w-auto p-2" align="start">
      <div class="flex gap-2 flex-wrap max-w-[240px]">
        <button
          v-for="(color, index) in defaultColors"
          :key="`default-${index}`"
          type="button"
          @click="selectColor(color)"
          :style="`background-color: ${color}`"
          :class="[
            'w-8 h-8 rounded-full border-2 transition-all',
            selectedColor === color ? 'border-gray-800 scale-110' : 'border-gray-300 hover:scale-105'
          ]"
          :title="color"
        />
        <button
          v-for="(color, index) in customColors"
          :key="`custom-${index}`"
          type="button"
          @click="selectColor(color)"
          @contextmenu.prevent="removeCustomColor(index)"
          :style="`background-color: ${color}`"
          :class="[
            'w-8 h-8 rounded-full border-2 transition-all relative group',
            selectedColor === color ? 'border-gray-800 scale-110' : 'border-gray-300 hover:scale-105'
          ]"
          :title="`${color} (right-click to remove)`"
        />
        <div class="relative">
          <button
            v-if="customColors.length < 10"
            type="button"
            @click="addCustomColor"
            class="w-8 h-8 rounded-full border-2 border-gray-300 hover:border-gray-400 flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-all"
            title="Add custom color"
          >
            <Plus class="w-4 h-4 text-gray-600" />
          </button>
          <input
            class="absolute top-0 left-0 w-0 h-0 overflow-hidden"
            type="color"
            @change="handleColorPickerChange"
            ref="colorPickerRef"
          />
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>